import React from "react";
import PropTypes from "prop-types";
import {
  pdf,
  Document,
  Page,
  Image,
  Text,
  StyleSheet,
  View,
} from "@react-pdf/renderer";
import QRCode from "qrcode";
import moment from "moment-timezone";

// NOTE: this current document is formatted for an Avery type sticker printer
// Once we are supporting more template types, we'll want to move this to its own file, and grab it based on type
const ValidationDocument = ({ merchantName, offerName, validations, facilityTimezone }) => {
  // Avery 5160/5260 geometry on US Letter (8.5 x 11in). Units are PDF points
  // (72pt = 1in). Letter page is 612 x 792pt. Label cell: 2.625in (189pt) wide
  // x 1in (72pt) tall; 3 columns x 10 rows. Top margin 0.5in (36pt), left margin
  // 0.1875in (13.5pt). Horizontal pitch 2.75in (198pt) = 189pt cell + 9pt gutter.
  const styles = StyleSheet.create({
    page: {
      paddingTop: 36,      // 0.5in Avery top margin
      paddingLeft: 13.5,   // 0.1875in Avery left margin
    },
    rootContainer: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
    },
    // One uniform cell for all three columns. Fixed 189pt x 72pt. The 9pt right
    // margin is the inter-column gutter (189 + 9 = 198pt horizontal pitch); it
    // is harmless on the 3rd column because the row wraps after it. paddingLeft
    // insets the content ~0.07in from the label's left edge for print tolerance
    // (it sits inside the fixed 189pt cell, so grid pitch/registration is
    // unchanged).
    cellContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",   // vertically center text column + QR together within the 1in cell
      width: 189,
      height: 72,
      marginRight: 9,
      paddingLeft: 5,
    },
    qrContainer: {
      display: "flex",
      justifySelf: "flex-end",
      alignItems: "center",
      flexShrink: 1,
    },
    qrCode: {
      height: "50px",
      width: "50px",
    },
    header: {
      marginBottom: 2,
    },
    defaultFont: {
      fontSize: "10px",
    },
    singleLine: {
      maxLines: 1,
      whiteSpace: "nowrap",
    },
    uses: {
      marginBottom: 2,
    },
    dateRange: {
      fontSize: "8px",
    },
    flexCenter: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    leftContent: {
      maxWidth: "1.9in",
      flexGrow: 1,
    },
  });

  return (
    <Document>
      {validations?.flatMap((row, index) => {
        return (
          <Page key={index} size="LETTER" style={styles.page}>
              <View style={styles.rootContainer}>
                  {row.map((validation, innerIndex) => {
                      return (
                        <View key={innerIndex} style={styles.cellContainer} wrap={false}>
                          <View style={styles.leftContent}>
                            <View style={[styles.header, styles.defaultFont]}>
                              <Text style={styles.singleLine}>{merchantName}</Text>
                            </View>
                            <View style={[styles.header, styles.defaultFont]}>
                              <Text style={styles.singleLine}>{offerName}</Text>
                            </View>
                            <View style={[styles.uses, styles.defaultFont]}>
                              <Text>{`Uses: ${validation.maxUses}`}</Text>
                            </View>
                            <View style={[styles.dateRange]}>
                              <Text>
                                {`${moment
                                  .utc(validation.activationDate)
                                  .tz(facilityTimezone)
                                  .format("MM/DD/YYYY hh:mm A")} - \n${moment
                                  .utc(validation.expirationDate)
                                  .tz(facilityTimezone)
                                  .format("MM/DD/YYYY hh:mm A")}`}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.qrContainer}>
                            <Image src={validation.barcodeValue} style={styles.qrCode} />
                          </View>
                        </View>
                      )            
                  })}
                  </View>
                </Page>
        )  
      })}
    </Document>
  );
};

// Exported for unit/property testing of the rendered document tree.
export { ValidationDocument };

// Chunks the validations into pages of 30 labels (3 columns x 10 rows per page).
// Pure function extracted to module scope so pagination behavior can be asserted
// directly in tests without running the full PDF render pipeline. Behavior is
// identical to the original inline implementation.
export const formatArray = (mutated) => {
  if (mutated?.length <= 0) return;

  let result = [];

  while (mutated.length) {
    result.push(mutated.splice(0, 30));
  }

  return result;
};

// Builds the download filename for the generated PDF. Exported so the
// `validations-{timestamp}.pdf` naming convention can be asserted in tests.
export const buildDownloadFilename = (timestamp = +new Date()) =>
  `validations-${timestamp}.pdf`;

ValidationDocument.propTypes = {
  merchantName: PropTypes.string.isRequired,
  offerName: PropTypes.string.isRequired,
  validations: PropTypes.arrayOf(
    PropTypes.shape({
      barcodeValue: PropTypes.string.isRequired,
      maxUses: PropTypes.number.isRequired,
      activationDate: PropTypes.string.isRequired,
      expirationDate: PropTypes.string.isRequired,
    })
  ),
  facilityTimezone: PropTypes.string.isRequired,
};

export default function useValidationPDFGenerator() {
  const generate = async (merchantName, offerName, content, facilityTimezone) => {
    const mutated = await Promise.all(
      content.map(async (validation) => {
        const result = await QRCode.toDataURL(validation.barcodeValue, {
          type: "png",
        });
        validation.barcodeValue = result;
        return validation;
      })
    );

    const doc = (
      <ValidationDocument
        merchantName={merchantName}
        offerName={offerName}
        validations={formatArray(mutated)}
        facilityTimezone={facilityTimezone}
      />
    );
    const asPdf = pdf([]);
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = buildDownloadFilename();
    link.click();
  };

  return {
    generate,
  };
}
